import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { useModelRegistry } from "@/hooks/useModelRegistry";
import type { AdminModel, ModelProvider } from "@/types/modelRegistry";
import { ModelDrawer } from "./model-registry/ModelDrawer";
import { ModelsTable } from "./model-registry/ModelsTable";
import { ProviderModal } from "./model-registry/ProviderModal";
import { RolesBar } from "./model-registry/RolesBar";
import { Btn, Panel, Skeleton } from "./model-registry/ui";

function Section({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-6">
        <div className="flex max-w-3xl flex-col gap-1">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </section>
  );
}

function KeyStatus({ provider }: { provider: ModelProvider }) {
  if (!provider.apiKeyEnv)
    return <span className="text-xs text-gray-400">no key needed</span>;
  return provider.keyConfigured ? (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
      <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
      <span className="font-mono">{provider.apiKeyEnv}</span> set
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
      <span className="size-1.5 rounded-full bg-amber-500" aria-hidden />
      <span className="font-mono">{provider.apiKeyEnv}</span> missing on server
    </span>
  );
}

function ProvidersList({
  providers,
  models,
  onEdit,
}: {
  providers: ModelProvider[] | null;
  models: AdminModel[] | null;
  onEdit: (provider: ModelProvider) => void;
}) {
  if (!providers) return <Skeleton className="h-24" />;
  const countFor = (id: string) =>
    models?.filter((m) => m.provider.id === id).length ?? 0;
  return (
    <Panel className="divide-y divide-gray-100">
      {providers.map((provider) => (
        <button
          key={provider.id}
          type="button"
          onClick={() => onEdit(provider)}
          className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/60"
        >
          <span className="w-44 shrink-0">
            <span className="block text-sm font-medium text-gray-900">
              {provider.name}
            </span>
            <span className="block text-xs text-gray-500">
              {countFor(provider.id)}{" "}
              {countFor(provider.id) === 1 ? "model" : "models"}
            </span>
          </span>
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-500">
            {provider.baseUrl ?? "SDK default URL"}
          </span>
          <span className="shrink-0">
            <KeyStatus provider={provider} />
          </span>
        </button>
      ))}
    </Panel>
  );
}

export default function ModelRegistryPage() {
  const registry = useModelRegistry();
  const [drawer, setDrawer] = useState<{
    open: boolean;
    model: AdminModel | null;
  }>({
    open: false,
    model: null,
  });
  const [providerModal, setProviderModal] = useState<{
    open: boolean;
    provider: ModelProvider | null;
  }>({
    open: false,
    provider: null,
  });

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 text-left">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-gray-900">Model Registry</h1>
        <p className="text-sm text-gray-500">
          The models the agent core can run, what they cost, and which one each
          job uses. Global for every workspace.
        </p>
      </header>

      <Section
        title="Defaults"
        description="Which model each job uses when nobody picked one."
      >
        <RolesBar
          roles={registry.roles}
          models={registry.models}
          onChanged={registry.refresh}
        />
      </Section>

      <Section
        title="Models"
        description="Active models show in the chat picker. Hidden ones only serve defaults. Click a name to edit; use the row menu to test, copy the id, or make it a default."
        action={
          <Btn size="sm" onClick={() => setDrawer({ open: true, model: null })}>
            <Plus className="size-4" />
            Add model
          </Btn>
        }
      >
        <ModelsTable
          models={registry.models}
          onEdit={(model) => setDrawer({ open: true, model })}
          onChanged={registry.refresh}
        />
      </Section>

      <Section
        title="Providers"
        description="Where requests go. A base URL and the env key that holds its secret. Click one to edit."
        action={
          <Btn
            size="sm"
            variant="outline"
            onClick={() => setProviderModal({ open: true, provider: null })}
          >
            <Plus className="size-4" />
            Add provider
          </Btn>
        }
      >
        <ProvidersList
          providers={registry.providers}
          models={registry.models}
          onEdit={(provider) => setProviderModal({ open: true, provider })}
        />
      </Section>

      <ModelDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        model={drawer.model}
        providers={registry.providers ?? []}
        onSaved={registry.refresh}
      />
      {providerModal.open && (
        <ProviderModal
          open={providerModal.open}
          onClose={() => setProviderModal((m) => ({ ...m, open: false }))}
          provider={providerModal.provider}
          onSaved={registry.refresh}
        />
      )}
    </div>
  );
}
